import {
  Avatar,
  AvatarFallback,
  cn,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  Flex,
  Icon,
  SidebarMenuButton,
  Text,
  useSidebar,
  useTheme,
  type IconType,
  type TTheme,
} from '@kognitos/lattice';
import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useBoolean } from 'usehooks-ts';

import { useGetOrganization } from '@/generated/hooks/agora/v1/organization';
import { useCurrentUser } from '@/generated/hooks/agora/v1/user';
import { useIsOnboardingOrInvitationPage } from '@/routes/util';
import { useAuth } from '@/shared/auth';
import { usePermissions } from '@/shared/permissions';
import {
  openOrganizationSwitcher,
  selectCurrentOrganizationId,
  selectCurrentWorkspaceId,
  useUserPreferencesStore,
} from '@/shared/stores';
import { getInitials } from '@/shared/utils';

import DropdownMenuItems from './DropdownMenuItems';
import { useIntercomMessenger } from './hooks/useIntercomMessenger';
import UserDetailsSkeleton from './UserDetailsSkeleton';
import type { TSidebarVariant } from './types';
import { getUserMenuItems } from './util';

const getThemeOptions = (
  t: ReturnType<typeof useTranslation>['t'],
): {
  value: TTheme;
  label: string;
  icon: IconType;
}[] =>
  [
    {
      value: 'light',
      label: t('theme.light'),
      icon: 'Sun',
    },
    {
      value: 'dark',
      label: t('theme.dark'),
      icon: 'Moon',
    },
    {
      value: 'system',
      label: t('theme.system'),
      icon: 'Monitor',
    },
  ] as const;

const UserDropdown = ({
  variant,
  onDropdownOpenChange,
}: {
  /** Picks the variant-specific entries (Audit, Roles & Access). */
  variant: TSidebarVariant;
  onDropdownOpenChange?: (open: boolean) => void;
}) => {
  const { user: authUser, logout, isAuthenticated } = useAuth();
  const { t: tNav } = useTranslation('navigation');
  const { t: tTheme } = useTranslation('theme');
  const { state: sidebarState } = useSidebar();
  const { theme, setTheme } = useTheme();
  const { isConfigured: isHelpConfigured, toggle: openHelp } =
    useIntercomMessenger();
  const [appearanceOpen, setAppearanceOpen] = useState(false);
  const { value: userOpen, toggle: toggleUser } = useBoolean();
  const isOnboardingOrInvitationPage = useIsOnboardingOrInvitationPage();

  const currentOrganizationId = useUserPreferencesStore(
    selectCurrentOrganizationId,
  );

  const currentWorkspaceId = useUserPreferencesStore(selectCurrentWorkspaceId);

  const {
    data: currentUser,
    isLoading: isCurrentUserLoading,
    isError: isCurrentUserError,
  } = useCurrentUser({
    query: {
      enabled: isAuthenticated && !isOnboardingOrInvitationPage,
      retry: false,
    },
  });

  const { data: currentOrg, isLoading: isOrgLoading } = useGetOrganization(
    currentOrganizationId,
    {
      query: {
        enabled:
          !!currentOrganizationId &&
          isAuthenticated &&
          !isOnboardingOrInvitationPage,
        retry: false,
      },
    },
  );

  const handleDropdownOpenChange = useCallback(
    (open: boolean) => {
      toggleUser();
      if (!open) {
        setAppearanceOpen(false);
      }
      onDropdownOpenChange?.(open);
    },
    [toggleUser, onDropdownOpenChange],
  );

  const handleThemeChange = useCallback(
    (newTheme: TTheme) => {
      setTheme(newTheme);
      setAppearanceOpen(false);
      handleDropdownOpenChange(false);
    },
    [setTheme, handleDropdownOpenChange],
  );

  // New users get a 403 here mid-onboarding. A fresh subscriber refetches the
  // failed query, which flips ProtectedRoute back to its loader, unmounting and
  // remounting this component in a loop.
  const { hasPermission, isLoading: isPermissionsLoading } = usePermissions({
    enabled: !isOnboardingOrInvitationPage,
  });
  // Without an org the entry stays, disabled, like its neighbours.
  const canAccessOrganizationSettings =
    !currentOrganizationId ||
    isPermissionsLoading ||
    hasPermission(['UpdateOrganization'], {
      organization: currentOrganizationId,
    });

  // Get menu items with proper callbacks
  const menuItems = getUserMenuItems({
    variant,
    currentOrganizationId,
    currentWorkspaceId,
    isCurrentUserError,
    onSelect: () => {
      handleDropdownOpenChange(false);
    },
    logout,
    openHelp,
    isHelpConfigured,
    canAccessOrganizationSettings,
  });

  // Intercept the switch organization item to add the org switcher logic
  const menuItemsWithSwitchOrg = menuItems.map(item => {
    if (item.key === 'switch-organization') {
      return {
        ...item,
        onClick: () => {
          openOrganizationSwitcher();
          handleDropdownOpenChange(false);
        },
      };
    }
    return item;
  });

  if (isCurrentUserLoading || isOrgLoading) {
    return <UserDetailsSkeleton />;
  }

  if (!isAuthenticated) {
    return null;
  }

  const userData = currentUser?.data.user;
  const orgData = currentOrg?.data;

  const userName = isOnboardingOrInvitationPage
    ? (authUser?.name ?? '')
    : (userData?.display_name ?? '');
  const organizationName = orgData?.title ?? '';

  // Appearance is an expandable submenu rather than a link, so it isn't in the
  // item list; it renders directly above Documentation.
  const appearanceIndex = menuItemsWithSwitchOrg.findIndex(
    item => item.key === 'documentation',
  );
  const itemsBeforeAppearance = menuItemsWithSwitchOrg.slice(
    0,
    appearanceIndex,
  );
  const itemsAfterAppearance = menuItemsWithSwitchOrg.slice(appearanceIndex);

  return (
    <DropdownMenu
      open={userOpen}
      onOpenChange={handleDropdownOpenChange}
      modal={false}
    >
      <DropdownMenuTrigger asChild>
        <SidebarMenuButton
          size="lg"
          data-testid="user-dropdown-trigger"
          className="app-sidebar-row app-sidebar-user-row"
        >
          <Flex align="center" width="full" className="min-w-0">
            <Flex
              align="center"
              justify="center"
              className="app-sidebar-row-icon w-10 shrink-0"
            >
              <Avatar className="size-6">
                <AvatarFallback className="rounded-full bg-primary text-[11px] font-semibold text-primary-foreground">
                  {getInitials(userName)}
                </AvatarFallback>
              </Avatar>
            </Flex>
            <Flex
              direction="column"
              align="start"
              flex="1"
              gap={0}
              className="app-sidebar-row-label min-w-0 text-left"
            >
              <Text
                level="small"
                className="w-full truncate text-[13px] text-sidebar-foreground"
              >
                {userName}
              </Text>
              <Text level="xSmall" color="muted" className="w-full truncate">
                {organizationName}
              </Text>
            </Flex>
          </Flex>
        </SidebarMenuButton>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        className="w-[248px]"
        sideOffset={sidebarState === 'collapsed' ? 16 : 12}
        onCloseAutoFocus={e => {
          // Prevent focus return to trigger to avoid focus ring artifacts
          e.preventDefault();
        }}
      >
        {/* Render items before appearance submenu */}
        <DropdownMenuItems items={itemsBeforeAppearance} />

        {/* Appearance submenu - special handling for expandable menu */}
        <DropdownMenuItem
          onClick={e => {
            e.preventDefault();
            e.stopPropagation();
            setAppearanceOpen(!appearanceOpen);
          }}
        >
          <Icon type="SunDim" size="sm" />
          {tNav('navigation.sidebar.appearance')}
          <Icon
            type="ChevronRight"
            className="ml-auto h-4 w-4 transition-transform duration-200"
            rotate={appearanceOpen ? 90 : undefined}
          />
        </DropdownMenuItem>
        <div
          className={cn(
            'overflow-hidden transition-all duration-200 ease-in-out',
            appearanceOpen
              ? 'max-h-48 opacity-100'
              : 'max-h-0 opacity-0 pointer-events-none',
          )}
        >
          {getThemeOptions(tTheme).map(option => (
            <DropdownMenuItem
              key={option.value}
              onClick={() => handleThemeChange(option.value)}
              className="pl-8"
            >
              <Icon type={option.icon} size="sm" />
              {option.label}
              {theme === option.value && (
                <Icon type="Check" className="ml-auto h-4 w-4" />
              )}
            </DropdownMenuItem>
          ))}
        </div>

        {/* Render items after appearance submenu */}
        <DropdownMenuItems items={itemsAfterAppearance} />
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default UserDropdown;
